<template>
	<div class="c-tooltip">
		<div ref="handle" class="c-tooltip-handle" v-on="events">
			<slot v-if="$slots.default" ref="follow"></slot>
			<i v-else class="tf">&#xe625;</i>
		</div>
		<div v-show="active" ref="tip" class="c-tooltip-content" :position="position" :style="positionStyle" v-on="events">
			<div class="c-tooltip-tip" :color="color" :style="{'width': parsePixel(this.width, true)}">
				<ins :position="position" :align="align" :style="angleStyle"></ins>
				<div v-if="$scopedSlots.tip" :style="{'max-width': parsePixel(this.width, true)}"><slot name="tip"></slot></div>
				<p v-else :style="{'max-width': parsePixel(this.width, true)}">{{tip}}</p>
			</div>
		</div>
	</div>
</template>

<script>
	function getStyleComputedProperty(element, property) {
		var css = window.getComputedStyle(element, null);
		return css[property];
	}
	
	function getScrollParent(element, fun) {
		var parent = element.parentNode;
	
		if (!parent) {
			return;
		}
		if (parent === window.document) {
			if (window.document.body.scrollTop || window.document.body.scrollLeft) {
				return fun(window.document.body);
			} else {
				return fun(window);
			}
		}
		
		if (
			['scroll', 'auto'].includes(getStyleComputedProperty(parent, 'overflow')) ||
			['scroll', 'auto'].includes(getStyleComputedProperty(parent, 'overflow-x')) ||
			['scroll', 'auto'].includes(getStyleComputedProperty(parent, 'overflow-y'))
		) {
			fun(parent);
		}
		return getScrollParent(element.parentNode, fun);
	}
	
	export default {
		name: 'cTooltip',
		
		props: {
			tip: String,

			width: [Number, String],

			position: {
				type: String,
				default: 'bottom'
			},

			align: {
				type: String,
				default: 'start'
			},

			color: {
				type: String,
				default: 'black'
			},

			zIndex: {
				type: [String, Number],
				default: 9999
			},

			clickEvent: Boolean
		},
		
		data() {
			return {
				active: false,
				positionStyle: {},
				angleStyle: {}
			}
		},

		computed: {
			events() {
				return {
					[this.clickEvent ? 'click' : 'mouseenter']: () => {
						if (this.clickEvent) {
							this._showEvent = true;
						}
						this.show();
					},
					[this.clickEvent ? '' : 'mouseleave']: this.hide
				}
			}
		},

		mounted() {
			document.body.appendChild(this.$refs.tip);
			if (this.clickEvent) {
				document.addEventListener('click', this.hide);
			}
		},
		
		beforeDestroy() {
			this._removeEvent && this._removeEvent();
			document.body.removeChild(this.$refs.tip);
			if (this.clickEvent) {
				document.removeEventListener('click', this.hide);
			}
		},

		methods: {
			show(position = this.position, align = this.align) {
				this.active = true;
				
				let follow = this.$refs.handle;
				follow = follow.$options ? follow.$el : follow;
				this._updatePosition = () => {
					let rect = follow.getBoundingClientRect();
					let tip = this.$refs.tip.getBoundingClientRect();
					let offset = 0;

					if (['top', 'bottom'].includes(position)) {
						this.angleStyle = {
							[align == 'start' ? 'left' : 'right']: Math.min(Math.max((rect.width - 10) / 2, 0), 10) + 'px'
						}
					} else {
						if (rect.height < 30 && tip.height <= 30) {
							offset = (30 - rect.height) / 2 * (align == 'start' ? -1 : 1);
							this.angleStyle = {
								top: (30 - 10) / 2 + 'px'
							}
						} else if (tip.height < rect.height) {
							offset = (tip.height - rect.height) / 2 * (align == 'start' ? -1 : 1);
							this.angleStyle = {
								top: (tip.height - 10) / 2 + 'px'
							}
						} else {
							this.angleStyle = {
								[align == 'start' ? 'top' : 'bottom']: Math.min(Math.max((rect.height - 10) / 2, 0), 10) + 'px'
							}
						}
					}

					this.positionStyle = {
						left: rect.left
							+ ({left: -tip.width, right: rect.width}[position] || 0)
							+ (['top', 'bottom'].includes(position) && align != 'start' ? rect.width - tip.width : 0)
							+ 'px',
						top: rect.top
							+ ({top: -tip.height, bottom: rect.height}[position] || 0)
							+ (['left', 'right'].includes(position) && align != 'start' ? rect.height - tip.height : 0)
							+ offset
							+ 'px',
						zIndex: this.zIndex
					}
				}
				this._removeEvent = () => {
					getScrollParent(follow, follow => {
						follow.removeEventListener('scroll', this._updatePosition);
					});
				}
				getScrollParent(follow, follow => {
					follow.addEventListener('scroll', this._updatePosition);
				});
				this.$nextTick(this._updatePosition);
			},

			hide() {
				if (this._showEvent) {
					this._showEvent = false;
					return;
				}
				this.active = false;
				this._removeEvent && this._removeEvent();
			}
		}
	}
</script>

<style lang="stylus" scoped>
	$blackBg = rgba(0,0,0,.8);
	$whiteBg = #fff;

	.c-tooltip {
		display: inline-block;

		&-handle {
			.c-icon-tip {
				font-size: 16px;
				color: #999;
				cursor: pointer;

				^[-1]:hover ^[-1..-1] {
					color: var(--color-main);
				}
			}
		}

		&-content {
			position: fixed;
			left: 50%;
			top: 50%;

			&[position=left] {
				padding-right: 8px;
			}

			&[position=right] {
				padding-left: 8px;
			}

			&[position=top] {
				padding-bottom: 8px;
			}

			&[position=bottom] {
				padding-top: 8px;
			}
		}

		&-tip {
			position: relative;
			border-radius: 2px;
			padding: 6px 10px;
			line-height: 18px;
			font-size: 13px;
			text-align: left;
			color: #fff;
			background: $blackBg;

			&[color=white] {
				color: #333;
				background: $whiteBg;
				box-shadow: 0 0 5px 0 rgba(0,0,0,.2);
				filter: drop-shadow(0 0 1px #ccc);

				> ins {
					&[position=left] {
						border-left-color: $whiteBg;
					}

					&[position=right] {
						border-right-color: $whiteBg;
					}

					&[position=bottom] {
						border-bottom-color: $whiteBg;
					}

					&[position=top] {
						border-top-color: $whiteBg;
					}
				}
			}
			
			> ins {
				position: absolute;

				&[position=left],
				&[position=right] {
					border-top: 5px solid transparent;
					border-bottom: 5px solid transparent;
				}

				&[position=left] {
					right: -5px;
					border-left: 5px solid $blackBg;
				}

				&[position=right] {
					left: -5px;
					border-right: 5px solid $blackBg;
				}

				&[position=bottom],
				&[position=top] {
					border-left: 5px solid transparent;
					border-right: 5px solid transparent;
				}

				&[position=bottom] {
					top: -5px;
					border-bottom: 5px solid $blackBg;
				}

				&[position=top] {
					bottom: -5px;
					border-top: 5px solid $blackBg;
				}
			}
		}
	}
</style>